import { Fragment, useCallback, useMemo } from 'react';
import { __OPERATION__EVENT__ADMIN__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { NonRecoverableErrorsClient } from './types';
import { useFormik } from 'formik';
import Table from 'shell/components/shared/table';
import { initialValues } from '..';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonNonRecoverableErrorsCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { authsObj, headCells, validationSchemaNonRecoverableErrorsFilter } from './constants';
import { Typography } from '@mui/material';
import PmiTitle from 'shell/components/shared/title';
import { noop } from 'shell/utils/noop';
import {
  NonRecoverableErrors,
  transformNonRecoverableErrors,
} from '../utils/transform-non-recoverable-errors';
import { NonRecoverableError } from 'shell/models/non-recoverable-errors';

const NonRecoverableErrorsListPage = (): React.ReactElement => {
  const [nonRecoverableErrors] = useHttpFetch<
    HttpResponseList<NonRecoverableError>,
    NonRecoverableErrors
  >(`${__OPERATION__EVENT__ADMIN__}/eventoInErrore/DLQ/list`, {
    onBootstrap: true,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformNonRecoverableErrors,
  });

  const filterNonRecoverableErrorsForm = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchemaNonRecoverableErrorsFilter,
    onSubmit: noop,
  });

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onVisibilityPress = useCallback(
    (cell): Promise<NonRecoverableErrorsClient> => {
      return new Promise((resolve) => {
        navigate(
          `/sistema/eventmanager/gestione-errori-non-recuperabili/dettaglio-errore-non-recuperabile/${cell.id}`
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onChangePage = useCallback(
    (page) => {
      filterNonRecoverableErrorsForm.setFieldValue('page', page || 0);
    },
    [filterNonRecoverableErrorsForm.setFieldValue]
  );

  const onChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterNonRecoverableErrorsForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };

      filterNonRecoverableErrorsForm.setValues(newDataset);
    },
    [filterNonRecoverableErrorsForm.setValues, filterNonRecoverableErrorsForm.values]
  );

  const count: number = useMemo(() => {
    return (nonRecoverableErrors.data as NonRecoverableErrors)?.pageInfo?.totalCount || 0;
  }, [nonRecoverableErrors.data]);
  return (
    <Fragment>
      <Breadcrumbs items={CommonNonRecoverableErrorsCrumbs} />
      <PmiTitle title="Gestione errori non recuperabili" />
      {nonRecoverableErrors.data ? (
        (nonRecoverableErrors.data as NonRecoverableErrors)?.nonRecoverableErrors?.length > 0 ? (
          <Table
            onSort={noop}
            head={headCells}
            orders={filterNonRecoverableErrorsForm.values.orders}
            activeActions={auths}
            page={filterNonRecoverableErrorsForm.values.page}
            rowsPerPage={filterNonRecoverableErrorsForm.values.rowsPerPage}
            body={(nonRecoverableErrors.data as NonRecoverableErrors)?.nonRecoverableErrors}
            onVisibilityPress={onVisibilityPress}
            count={count || 0}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            ariaLabel="Ricerca errori non recuperabili"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

NonRecoverableErrorsListPage.displayName = 'Non Recoverable Errors List page component';

export default NonRecoverableErrorsListPage;
